import React, { useState } from "react";
import Sidebar from "../../Components/Sidebar/Sidebar";
import Header from "../../Components/Header/Header";
import classes from "./Appointments.module.css";
import Newappointment from "./Newappointment";
import {
    FaCalendarCheck, FaCalendarWeek, FaCalendarPlus, FaCheckCircle, FaTimesCircle,
    FaArrowUp, FaArrowDown, FaChevronLeft, FaChevronRight, FaUser, FaPlus, FaFileAlt,
    FaCalendarAlt
} from "react-icons/fa";

/* ===========================
   DATA & CONFIG
=========================== */
const statsData = [
    { title: "Today's Appointments", val: "28", change: "12%", dir: "up", color: "#2563eb", bg: "#eff6ff", icon: <FaCalendarCheck /> },
    { title: "This Week", val: "156", change: "8%", dir: "up", color: "#059669", bg: "#ecfdf5", icon: <FaCalendarWeek /> },
    { title: "Upcoming", val: "342", change: "10%", dir: "up", color: "#7c3aed", bg: "#f5f3ff", icon: <FaCalendarPlus /> },
    { title: "Completed (This Week)", val: "128", change: "15%", dir: "up", color: "#f59e0b", bg: "#fef3c7", icon: <FaCheckCircle /> },
    { title: "Cancelled (This Week)", val: "18", change: "5%", dir: "down", color: "#ef4444", bg: "#fef2f2", icon: <FaTimesCircle /> },
];

const ALL_DOCTORS = [
    { name: "Dr. James Wilson", specialty: "Cardiology" },
    { name: "Dr. Sarah Ahmed", specialty: "General Medicine" },
    { name: "Dr. Michael Lee", specialty: "Orthopedics" },
    { name: "Dr. Priya Sharma", specialty: "Pediatrics" }
];

const timeSlots = ["9:00 AM", "10:00 AM", "11:00 AM", "12:00 PM", "1:00 PM", "2:00 PM", "3:00 PM", "4:00 PM", "5:00 PM"];
const weekDays = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

/* Generate Calendar Days for the mini calendar */
const generateCalendarDays = () => {
    const days = [];
    for (let i = 28; i <= 31; i++) days.push(i);
    for (let i = 1; i <= 30; i++) days.push(i);
    for (let i = 1; i <= 2; i++) days.push(i);
    return days;
};
const calendarDays = generateCalendarDays();

/* Doctor Color Map for the visual sync */
const doctorColors = {
    "Dr. James Wilson": { bg: "#EFF6FF", border: "#3B82F6", text: "#1D4ED8" },
    "Dr. Sarah Ahmed": { bg: "#ECFDF5", border: "#10B981", text: "#047857" },
    "Dr. Michael Lee": { bg: "#FEF3C7", border: "#F59E0B", text: "#B45309" },
    "Dr. Priya Sharma": { bg: "#F3E8FF", border: "#8B5CF6", text: "#6D28D9" },
};

/* Upcoming Appointments Data */
const upcomingAppts = [
    { name: "John Doe", id: "OPD - 12345", doc: "Dr. James Wilson", status: "Confirmed", time: "09:00 AM" },
    { name: "Mary Jane", id: "IPD - 54321", doc: "Dr. Sarah Ahmed", status: "Confirmed", time: "09:00 AM" },
    { name: "Christopher Martin", id: "OPD - 67890", doc: "Dr. Michael Lee", status: "Confirmed", time: "09:00 AM" },
    { name: "Sophia Wilson", id: "OPD - 11223", doc: "Dr. Priya Sharma", status: "Confirmed", time: "09:00 AM" },
];

/* Status Data for Donut Chart */
const statusData = [
    { label: "Confirmed", val: 24, pct: 85.7, color: "#22c55e" },
    { label: "Pending", val: 3, pct: 10.7, color: "#f59e0b" },
    { label: "Cancelled", val: 1, pct: 3.6, color: "#ef4444" },
    { label: "Completed", val: 0, pct: 0, color: "#2563eb" },
];

/* ===========================
   MAIN COMPONENT
=========================== */
export default function Appointments() {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [view, setView] = useState("Day");
    const [date, setDate] = useState("21 May 2024");

    /* --- FILTER STATES --- */
    const [filters, setFilters] = useState({
        doctor: "All Doctors",
        department: "All Departments",
        location: "All Locations",
        type: "All Types",
        status: "All Status"
    });
    
    /* The list of doctors currently displayed based on filters */
    const [displayedDoctors, setDisplayedDoctors] = useState(ALL_DOCTORS);

    /* --- FILTER HANDLERS --- */
    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
    };

    const handleApplyFilters = () => {
        let filtered = ALL_DOCTORS;

        // 1. Filter by Doctor Name
        if (filters.doctor !== "All Doctors") {
            filtered = filtered.filter(doc => doc.name === filters.doctor);
        }

        // 2. Filter by Department
        if (filters.department !== "All Departments") {
            filtered = filtered.filter(doc => doc.specialty === filters.department);
        }

        // Note: Location, Type, and Status usually filter the specific appointment slots,
        // not the doctor columns themselves. For this demo, I'm applying them to the displayed doctors
        // but you can expand on this logic if you have a global appointments list.
        
        // Simple random simulation for other filters to show it works
        if (filters.status !== "All Status" && filtered.length > 0) {
           // We just pass it through. In a real app, you would filter an array of appointment objects here.
        }

        setDisplayedDoctors(filtered);
    };

    const handleResetFilters = () => {
        setFilters({
            doctor: "All Doctors",
            department: "All Departments",
            location: "All Locations",
            type: "All Types",
            status: "All Status"
        });
        setDisplayedDoctors(ALL_DOCTORS);
    };

    return (
        <div className={classes.page}>
            <Sidebar />
            <div className={classes.mainWrapper}>
                <Header />
                <div className={classes.contentArea}>

                    {/* --- HEADER & STATS --- */}
                    <div className={classes.topSection}>
                        <div>
                            <h1 className={classes.pageTitle}>Appointments</h1>
                            <p className={classes.breadcrumb}>Manage patient appointments and schedule</p>
                        </div>
                        <div className={classes.topButtons}>
                            <button className={classes.btnPrimary} onClick={() => setIsModalOpen(true)}>
                                <FaPlus /> New Appointment
                            </button>
                            <button className={classes.btnOutline}><FaFileAlt /> Reports <FaChevronRight /></button>
                        </div>
                    </div>

                    <div className={classes.statsGrid}>
                        {statsData.map((s, i) => (
                            <div key={i} className={classes.statCard}>
                                <div className={classes.iconBox} style={{ backgroundColor: s.bg, color: s.color }}>
                                    {s.icon}
                                </div>
                                <div className={classes.statContent}>
                                    <p className={classes.statTitle}>{s.title}</p>
                                    <div className={classes.statValue}>{s.val}</div>
                                    <div className={`${classes.statChange} ${s.dir === 'up' ? classes.trendUp : classes.trendDown}`}>
                                        {s.dir === 'up' ? <FaArrowUp /> : <FaArrowDown />}
                                        <span>{s.change} from {s.title.includes('Today') ? 'yesterday' : 'last week'}</span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* --- 3-COLUMN GRID --- */}
                    <div className={classes.middleSection}>

                        {/* --- LEFT: FILTERS --- */}
                        <div className={classes.filterPanel}>
                            <h3>Filters <span className={classes.resetLink} onClick={handleResetFilters}>Reset</span></h3>
                            
                            <div className={classes.filterGroup}>
                                <label>Date</label>
                                <input type="text" value={date} readOnly className={classes.filterInput} />
                            </div>
                            
                            <div className={classes.filterGroup}>
                                <label>Doctor</label>
                                <select name="doctor" value={filters.doctor} onChange={handleFilterChange} className={classes.filterSelect}>
                                    <option>All Doctors</option>
                                    {ALL_DOCTORS.map(d => <option key={d.name} value={d.name}>{d.name}</option>)}
                                </select>
                            </div>
                            
                            <div className={classes.filterGroup}>
                                <label>Department</label>
                                <select name="department" value={filters.department} onChange={handleFilterChange} className={classes.filterSelect}>
                                    <option>All Departments</option>
                                    {[...new Set(ALL_DOCTORS.map(d => d.specialty))].map(spec => (
                                        <option key={spec} value={spec}>{spec}</option>
                                    ))}
                                </select>
                            </div>
                            
                            <div className={classes.filterGroup}>
                                <label>Location</label>
                                <select name="location" value={filters.location} onChange={handleFilterChange} className={classes.filterSelect}>
                                    <option>All Locations</option>
                                    <option>Main Clinic</option>
                                    <option>Downtown Clinic</option>
                                </select>
                            </div>
                            
                            <div className={classes.filterGroup}>
                                <label>Appointment Type</label>
                                <select name="type" value={filters.type} onChange={handleFilterChange} className={classes.filterSelect}>
                                    <option>All Types</option>
                                    <option>General Checkup</option>
                                    <option>Specialist</option>
                                </select>
                            </div>
                            
                            <div className={classes.filterGroup}>
                                <label>Status</label>
                                <select name="status" value={filters.status} onChange={handleFilterChange} className={classes.filterSelect}>
                                    <option>All Status</option>
                                    <option>Confirmed</option>
                                    <option>Pending</option>
                                    <option>Cancelled</option>
                                </select>
                            </div>
                            
                            <button className={classes.btnApplyFilters} onClick={handleApplyFilters}>Apply Filters</button>
                        </div>

                        {/* --- MIDDLE: SCHEDULE --- */}
                        <div className={classes.schedulePanel}>
                            <div className={classes.scheduleNav}>
                                <div className={classes.viewToggles}>
                                    {['Day', 'Week', 'Month'].map(m => (
                                        <button key={m} className={`${classes.viewBtn} ${view === m ? classes.activeView : ''}`} onClick={() => setView(m)}>
                                            {m}
                                        </button>
                                    ))}
                                </div>
                                <div className={classes.dateNav}>
                                    <button className={classes.navBtn}><FaChevronLeft /></button>
                                    <span><FaCalendarAlt /> {date}, Tuesday</span>
                                    <button className={classes.navBtn}><FaChevronRight /></button>
                                    <button className={classes.todayBtn}>Today</button>
                                </div>
                            </div>

                            <table className={classes.gridTable}>
                                <thead>
                                    <tr>
                                        <th style={{ width: '80px' }}>Time</th>
                                        {/* Displaying filtered doctors here */}
                                        {displayedDoctors.length > 0 ? (
                                            displayedDoctors.map(d => (
                                                <th key={d.name}>{d.name}<span>{d.specialty}</span></th>
                                            ))
                                        ) : (
                                            <th colSpan="4" style={{textAlign:'center', color:'#9CA3AF', padding: '20px'}}>
                                                No doctors match the selected filters.
                                            </th>
                                        )}
                                    </tr>
                                </thead>
                                <tbody>
                                    {timeSlots.map((time) => (
                                        <tr key={time}>
                                            <td className={classes.timeLabel}>{time}</td>
                                            {displayedDoctors.map((doc, dIdx) => {
                                                let isLunch = time === "12:00 PM";
                                                let isAppt = !isLunch && Math.random() > 0.25;
                                                let status = isAppt ? (Math.random() > 0.2 ? "Confirmed" : Math.random() > 0.5 ? "Pending" : "Cancelled") : "";
                                                
                                                const docColor = doctorColors[doc.name];

                                                if (isLunch) return (
                                                    <td key={dIdx}>
                                                        <div className={`${classes.apptCard} ${classes.lunchCard}`}>
                                                            12:00 PM - 01:00 PM<br />Lunch Break
                                                        </div>
                                                    </td>
                                                );
                                                if (!isAppt) return <td key={dIdx}></td>;

                                                return (
                                                    <td key={dIdx}>
                                                        <div className={classes.apptCard} style={{ backgroundColor: docColor.bg, borderColor: docColor.border }}>
                                                            <h5>Patient {dIdx + 1}</h5>
                                                            <p>{time}</p>
                                                            <span className={classes.apptStatusLabel} style={{ color: status === 'Confirmed' ? '#1D4ED8' : status === 'Pending' ? '#B45309' : '#DC2626' }}>
                                                                {status}
                                                            </span>
                                                        </div>
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>

                            <div className={classes.scheduleLegend}>
                                <span><span className={classes.legendDot} style={{ background: '#22c55e' }}></span> Confirmed</span>
                                <span><span className={classes.legendDot} style={{ background: '#f59e0b' }}></span> Pending</span>
                                <span><span className={classes.legendDot} style={{ background: '#ef4444' }}></span> Cancelled</span>
                                <span><span className={classes.legendDot} style={{ background: '#2563eb' }}></span> Completed</span>
                            </div>
                        </div>

                        {/* --- RIGHT: SIDEBAR --- */}
                        <div className={classes.rightSection}>
                            <div className={classes.sidebarCard}>
                                <h3>Mini Calendar <span className={classes.cardAction}><FaChevronLeft /> <FaChevronRight /></span></h3>
                                <div className={classes.miniCalendarGrid}>
                                    {weekDays.map(d => <div key={d} className={classes.weekDayLabel}>{d}</div>)}
                                    {calendarDays.map((d, i) => (
                                        <div key={i} className={`${classes.calDay} ${i < 4 || i > 34 ? classes.otherMonth : ''} ${d === 21 ? classes.todayCal : ''}`}>
                                            {d}
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className={classes.sidebarCard}>
                                <h3>Upcoming Appointments <span className={classes.cardAction}>View All</span></h3>
                                <div className={classes.upcomingList}>
                                    {upcomingAppts.map((a, i) => {
                                        const docColor = doctorColors[a.doc];
                                        return (
                                            <div key={i} className={classes.upcomingItem}>
                                                <div className={classes.upcomingColorBar} style={{ backgroundColor: docColor.border }}></div>
                                                <div className={classes.upcomingInfo}>
                                                    <h4>{a.name}</h4>
                                                    <p>{a.id}</p>
                                                    <p className={classes.upcomingDoc}>{a.doc}</p>
                                                    <span className={classes.upcomingStatus}>{a.status}</span>
                                                </div>
                                                <div className={classes.upcomingTime}>
                                                    <strong>{a.time}</strong>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>

                            <div className={classes.sidebarCard}>
                                <h3>Appointment Status (Today)</h3>
                                <div className={classes.donutContainer}>
                                    <div className={classes.donutChart}></div>
                                    <div className={classes.donutCenter}>
                                        28 <small>Total</small>
                                    </div>
                                </div>
                                <div className={classes.statusList}>
                                    {statusData.map((s, i) => (
                                        <div key={i} className={classes.statusRow}>
                                            <div className={classes.statusLabel}>
                                                <span className={classes.statusDot} style={{ background: s.color }}></span>
                                                {s.label}
                                            </div>
                                            <div className={classes.statusValue}>
                                                <strong>{s.val}</strong>
                                                <span>({s.pct}%)</span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            
            <Newappointment isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
        </div>
    );
}